import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import serviceApi from '../services/serviceApi';
import PhoneInput from './PhoneInput';
import { CLIENT_TYPE_LABELS } from '../config/serviceConstants';

const EditClientModal = ({ show, onHide, client, onUpdated }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    nationalId: '',
    type: 'residential',
    status: 'active',
    companyName: '',
    notes: '',
  });

  // Pre-populate from client on open
  useEffect(() => {
    if (client && show) {
      setForm({
        name: client.userId?.name || '',
        phone: client.userId?.phone || '',
        nationalId: client.userId?.nationalId || '',
        type: client.type || 'residential',
        status: client.status || 'active',
        companyName: client.companyName || '',
        notes: client.notes || '',
      });
      setError(null);
    }
  }, [client, show]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('اسم العميل مطلوب');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      // Dirty check — only send changed fields
      const updates = {};

      if (form.name.trim() !== (client.userId?.name || '')) updates.name = form.name.trim();
      if (form.phone !== (client.userId?.phone || '')) updates.phone = form.phone;
      if (form.nationalId.trim() !== (client.userId?.nationalId || '')) updates.nationalId = form.nationalId.trim();
      if (form.type !== client.type) updates.type = form.type;
      if (form.status !== client.status) updates.status = form.status;
      if (form.companyName.trim() !== (client.companyName || '')) updates.companyName = form.companyName.trim();
      if (form.notes.trim() !== (client.notes || '')) updates.notes = form.notes.trim();

      if (Object.keys(updates).length === 0) {
        onHide();
        return;
      }

      await serviceApi.updateClient(client._id, updates);
      onUpdated();
      onHide();
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ — يرجى المحاولة مرة أخرى');
    } finally {
      setSaving(false);
    }
  };

  if (!client) return null;

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#471396' }}>
          تعديل بيانات العميل
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {/* Contact info */}
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#471396', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          بيانات التواصل
        </div>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الاسم</Form.Label>
          <Form.Control
            className="touch-input"
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>رقم الجوال</Form.Label>
          <PhoneInput
            value={form.phone}
            onChange={(val) => handleChange('phone', val)}
            defaultCountry="SA"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>رقم الهوية الوطنية <span style={{ color: '#6c757d', fontWeight: 400 }}>(اختياري)</span></Form.Label>
          <Form.Control
            className="touch-input"
            type="text"
            value={form.nationalId}
            onChange={(e) => handleChange('nationalId', e.target.value)}
            dir="ltr"
            placeholder="1xxxxxxxxx"
          />
        </Form.Group>

        <hr style={{ margin: '16px 0' }} />

        {/* Account classification */}
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#471396', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          تصنيف الحساب
        </div>

        <Row className="mb-3">
          <Col xs={6}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>نوع العميل</Form.Label>
              <Form.Select
                className="touch-input"
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الحالة</Form.Label>
              <Form.Select
                className="touch-input"
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="active">نشط</option>
                <option value="inactive">غير نشط</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>اسم الشركة <span style={{ color: '#6c757d', fontWeight: 400 }}>(اختياري)</span></Form.Label>
          <Form.Control
            className="touch-input"
            type="text"
            value={form.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>ملاحظات داخلية <span style={{ color: '#6c757d', fontWeight: 400 }}>(اختياري)</span></Form.Label>
          <Form.Control
            className="touch-input"
            as="textarea"
            rows={3}
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="ملاحظات للفريق الداخلي فقط..."
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'حفظ التعديلات'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditClientModal;
